import { SBAsyncSelect } from '@supplement-bacon/react-async-select'
import { Entity } from '@utils/Entities'
import { DatePicker, Form, InputNumber, Space } from 'antd'
import dayjs from 'dayjs'
import PhonePrefixInput from './PhonePrefixInput'
import {
  CriteriaCategory,
  SegmentCriteriaPayload,
  SegmentFormValues,
  SegmentNode,
} from './types'

const { RangePicker } = DatePicker

/**
 * Centralized configuration of the segmentation criteria.
 * Makes adding, editing and removing criteria straightforward.
 */
export const getCriteriaConfig = (): CriteriaCategory[] => [
  {
    categoryTitle: 'Reservations',
    key: 'reservations',
    items: [
      {
        title: 'Total number of reservations',
        description: 'Filter by the number of reservations made',
        fieldName: 'reservationCount',
        component: (
          <Space.Compact style={{ width: '100%' }}>
            {/*
              A minimum of 0 reads as a filter but keeps everyone, so the API
              floors it at 1. A maximum of 0 is meaningful — it selects the
              users who never booked — and stays allowed.
            */}
            <Form.Item
              name={['reservationCount', 'min']}
              noStyle
              rules={[{ type: 'number', min: 1 }]}
            >
              <InputNumber
                min={1}
                placeholder="Minimum"
                style={{ width: '50%' }}
              />
            </Form.Item>
            <Form.Item
              name={['reservationCount', 'max']}
              noStyle
              rules={[{ type: 'number', min: 0 }]}
            >
              <InputNumber
                min={0}
                placeholder="Maximum"
                style={{ width: '50%' }}
              />
            </Form.Item>
          </Space.Compact>
        ),
      },
      {
        title: 'Venues already booked',
        description: 'Venues where the client has already booked',
        fieldName: 'visitedVenues',
        component: (
          <Form.Item name="visitedVenues" noStyle>
            <SBAsyncSelect
              entity={Entity.venue}
              multiple
              placeholder="Select venues"
            />
          </Form.Item>
        ),
      },
      {
        title: 'Venues not booked yet',
        description: 'Venues where the client has never booked',
        fieldName: 'unvisitedVenues',
        component: (
          <Form.Item name="unvisitedVenues" noStyle>
            <SBAsyncSelect
              entity={Entity.venue}
              multiple
              placeholder="Select venues"
            />
          </Form.Item>
        ),
      },
      {
        title: 'Average spend per reservation',
        description: 'Filter by the average amount spent per reservation',
        fieldName: 'amountSpent',
        component: (
          <Space.Compact style={{ width: '100%' }}>
            <Form.Item
              name={['amountSpent', 'min']}
              noStyle
              rules={[{ type: 'number', min: 0 }]}
            >
              <InputNumber
                min={0}
                placeholder="Minimum (€)"
                style={{ width: '50%' }}
                addonAfter="€"
              />
            </Form.Item>
            <Form.Item
              name={['amountSpent', 'max']}
              noStyle
              rules={[{ type: 'number', min: 0 }]}
            >
              <InputNumber
                min={0}
                placeholder="Maximum (€)"
                style={{ width: '50%' }}
                addonAfter="€"
              />
            </Form.Item>
          </Space.Compact>
        ),
      },
    ],
  },
  {
    categoryTitle: 'User',
    key: 'user',
    items: [
      {
        title: 'Registration date',
        description: "Client's registration period",
        fieldName: 'registrationDate',
        component: (
          <Form.Item name="registrationDate" noStyle>
            <RangePicker
              style={{ width: '100%' }}
              placeholder={['Start date', 'End date']}
              format="DD/MM/YYYY"
            />
          </Form.Item>
        ),
      },
      {
        title: 'Phone prefix',
        description: 'Filter by one or more country calling codes',
        fieldName: 'phonePrefix',
        component: <PhonePrefixInput />,
      },
    ],
  },
  // Additional categories can easily be added here
  // {
  //   categoryTitle: 'Behavior',
  //   key: 'behavior',
  //   items: [...]
  // },
]

const isSet = (value?: number | null): value is number =>
  value !== undefined && value !== null

/**
 * Validates the criteria values on submit.
 *
 * `validateFields` only walks the MOUNTED fields, so a criterion left
 * collapsed never runs its `Form.Item` rules while its value is still
 * submitted: this covers the same bounds for the whole tree, plus the
 * cross-field ones no single field can express.
 */
export const validateSegmentForm = (
  values: Partial<SegmentFormValues>
): string[] => {
  const errors: string[] = []

  // Reservation-count validation
  if (values.reservationCount) {
    const { min, max } = values.reservationCount
    if (isSet(min) && min < 1) {
      // A minimum of 0 reads as a filter but keeps everyone; a maximum of 0
      // is meaningful (the users who never booked) and stays allowed.
      errors.push('The minimum number of reservations must be at least 1')
    }
    if (isSet(max) && max < 0) {
      errors.push('The maximum number of reservations cannot be negative')
    }
    if (isSet(min) && isSet(max) && min > max) {
      errors.push(
        'The minimum number of reservations cannot be greater than the maximum'
      )
    }
  }

  // Amount-spent validation
  if (values.amountSpent) {
    const { min, max } = values.amountSpent
    if ((isSet(min) && min < 0) || (isSet(max) && max < 0)) {
      errors.push('The amount spent cannot be negative')
    }
    if (isSet(min) && isSet(max) && min > max) {
      errors.push('The minimum amount cannot be greater than the maximum')
    }
  }

  return errors
}

/**
 * Checks whether a criterion is active (has values).
 */
export const isCriteriaActive = (fieldName: string, values: any): boolean => {
  const value = values[fieldName]

  if (!value) return false

  if (Array.isArray(value)) {
    return value.length > 0
  }

  if (typeof value === 'object') {
    return Object.values(value).some(
      (v) => v !== undefined && v !== null && v !== ''
    )
  }

  return true
}

const hasBound = (range?: { min?: number; max?: number }): boolean =>
  range !== undefined &&
  ((range.min !== undefined && range.min !== null) ||
    (range.max !== undefined && range.max !== null))

const hasItems = (ids?: unknown[]): ids is unknown[] =>
  Array.isArray(ids) && ids.length > 0

/**
 * Normalizes a date value (Dayjs from the RangePicker, Date or string) to
 * ISO 8601, the format the backend expects (`users.created_at >= from`).
 */
const toIso = (value: unknown): string | undefined => {
  if (value == null) return undefined
  if (typeof value === 'string') return value
  if (
    typeof (value as { toISOString?: () => string }).toISOString === 'function'
  ) {
    return (value as { toISOString: () => string }).toISOString()
  }
  return String(value)
}

/**
 * Turns the form values into an API payload (flat AND).
 *
 * Only active conditions are included. Each active field becomes a typed leaf
 * aligned with the backend `CriteriaConditionType` enum (kebab-case types,
 * `children` rather than `conditions`, `phone-prefix.prefix`).
 */
export const buildCriteriaPayload = (
  values: SegmentFormValues
): SegmentCriteriaPayload => {
  const children: SegmentNode[] = []

  if (hasBound(values.reservationCount)) {
    children.push({
      type: 'reservations-count',
      min: values.reservationCount?.min,
      max: values.reservationCount?.max,
    })
  }

  if (hasBound(values.amountSpent)) {
    children.push({
      type: 'average-amount-spent',
      min: values.amountSpent?.min,
      max: values.amountSpent?.max,
    })
  }

  if (hasItems(values.visitedVenues)) {
    children.push({
      type: 'venues-booked',
      venueIds: values.visitedVenues!.map(Number),
    })
  }

  if (hasItems(values.unvisitedVenues)) {
    children.push({
      type: 'venues-never-booked',
      venueIds: values.unvisitedVenues!.map(Number),
    })
  }

  const from = toIso(values.registrationDate?.[0])
  const to = toIso(values.registrationDate?.[1])
  if (from && to) {
    children.push({ type: 'registration-date', from, to })
  }

  // A phone can start with only one prefix, so several prefixes are OR'd: one
  // leaf for a single prefix, an `or` group for several.
  const prefixes = values.phonePrefix ?? []
  if (prefixes.length === 1) {
    children.push({ type: 'phone-prefix', prefix: prefixes[0] })
  } else if (prefixes.length > 1) {
    children.push({
      operator: 'or',
      children: prefixes.map((prefix) => ({
        type: 'phone-prefix',
        prefix,
      })),
    })
  }

  return { operator: 'and', children }
}

/**
 * Venue ids travel as integers: `AudienceCriteria` rejects a stringified id
 * (`is_int`), so a segment holding a venue criterion would come back 422. The
 * select already works with the numeric ids it fetches, but a stored tree is
 * read back from JSON, so coerce rather than trust.
 */
const toVenueId = (id: number | string): string | number => {
  const asNumber = Number(id)
  return id !== '' && !Number.isNaN(asNumber) ? asNumber : id
}

/**
 * Inverse of `buildCriteriaPayload`: rehydrates form values from a stored
 * criteria tree, so editing a segment starts from what it currently targets.
 *
 * The shapes this form can render are mapped back: the flat-AND leaves and the
 * single `or` group of phone-prefix leaves. Anything else (other groups,
 * `timeWindow` — Tier 2) is ignored rather than half-rendered.
 */
export const parseCriteriaPayload = (
  criteria?: SegmentCriteriaPayload | null
): Partial<SegmentFormValues> => {
  const values: Partial<SegmentFormValues> = {}

  const addPrefixes = (...prefixes: string[]) => {
    if (prefixes.length) {
      values.phonePrefix = [...(values.phonePrefix ?? []), ...prefixes]
    }
  }

  for (const node of criteria?.children ?? []) {
    // The only group this form emits is an `or` of phone-prefix leaves.
    if ('operator' in node) {
      addPrefixes(
        ...node.children.flatMap((child) =>
          child.type === 'phone-prefix' ? [child.prefix] : []
        )
      )
      continue
    }

    switch (node.type) {
      case 'reservations-count':
        values.reservationCount = { min: node.min, max: node.max }
        break
      case 'average-amount-spent':
        values.amountSpent = { min: node.min, max: node.max }
        break
      case 'venues-booked':
        values.visitedVenues = (node.venueIds ?? []).map(toVenueId)
        break
      case 'venues-never-booked':
        values.unvisitedVenues = (node.venueIds ?? []).map(toVenueId)
        break
      case 'registration-date':
        if (node.from && node.to) {
          values.registrationDate = [dayjs(node.from), dayjs(node.to)]
        }
        break
      case 'phone-prefix':
        addPrefixes(node.prefix)
        break
    }
  }

  return values
}
